import {TriggerJson} from "../TriggerEditor";
import {Task} from "../../api/types";
import exp from "node:constants";
import {agent} from "../../api/agent";

export interface PipelineDefinition {
    type: string;
    fields?: Record<string, FieldType>;
    possibleNext?: string[];
    generateEmptyElement?: () => PipelineElement;
    addChildren?: (type: string, element: PipelineElement) => void;
    addChildren2?: (element: PipelineElement, target: PipelineElement) => void;
    getChildren?: (element: PipelineElement) => PipelineElement[];
    getFinalElement: (element: PipelineElement) => any;
    parseElement: (element: any) => PipelineElement[];
    preview?: (element: PipelineElement) => string;
    moveChildren?: (element: PipelineElement, fromIndex: number, toIndex: number) => void;
    removeChildren?: (element: PipelineElement, child: PipelineElement) => void;
    canBeRemoved?: (element: PipelineElement) => boolean;
}

interface FieldType {
    type: 'secret' | 'string' | 'dropdown' | 'variable' | 'list' | 'multiselect' | 'cmd' | 'trigger' | 'repo' | 'grapsecrets' | 'addtestresults' | 'boolean' | 'branches';
    label: string;
    placeholder?: string;
    options?: string[]; // For dropdowns
}

export let tasks: Task[] | null = null;
export let tasksLoaded = false;

export const setTasks = (newTasks: Task[] | null) => {
    if (!newTasks) {
        console.warn('No tasks available');
        tasksLoaded = false;
        return;
    }
    tasks = newTasks;

    piplineConfig.find(value => value.type === 'task')!.fields!.tasks.options = ['none', ...newTasks.map(task => task.name)];
    tasksLoaded = true;
}

let tasksLoadPromise: Promise<void> | null = null;

const ensureTasksLoaded = (): Promise<void> => {
    if (tasksLoaded) {
        return Promise.resolve();
    }
    if (!tasksLoadPromise) {
        tasksLoadPromise = agent.getTasks().then(value => {
            if (value) {
                setTasks(value);
            }
        }).finally(() => {
            tasksLoadPromise = null;
        });
    }
    return tasksLoadPromise;
}

export const convertFinal = (element: PipelineElement): any => {
    const config = piplineConfig.find(config => config.type === element.type);
    if (!config) {
        throw new Error(`No configuration found for type: ${element.type}`);
    }
    const finalElement = config!.getFinalElement(element);
    if (element.deployment) {
        finalElement.deployment = true;
    }
    return finalElement;
}

const _convertFromFinal = (finalElement: any): PipelineElement[] => {
    const config = piplineConfig.find(config => config.type === finalElement.type);
    if (!config) {
        throw new Error(`No configuration found for type: ${finalElement.type}`);
    }
    const elements = config.parseElement(finalElement);
    if (finalElement.deployment) {
        elements.forEach(element => element.deployment = true);
    }
    return elements;
}

const deduplicateElementId = (element: PipelineElement, seenIds: Set<string>): void => {
    if (seenIds.has(element.id)) {
        element.id = crypto.randomUUID();
    }
    seenIds.add(element.id);

    const config = piplineConfig.find(config => config.type === element.type);
    if (!config || !config.getChildren) return;
    for (const child of config.getChildren(element)) {
        deduplicateElementId(child, seenIds);
    }
}

// Replaces any id that appears more than once within the given elements (and their
// descendants) with a freshly generated one, e.g. when a task referenced twice in the
// same pipeline brings in the same ids from its template CI on each embed.
export const deduplicateIds = (elements: PipelineElement[], seenIds: Set<string> = new Set()): PipelineElement[] => {
    for (const element of elements) {
        deduplicateElementId(element, seenIds);
    }
    return elements;
}

export const convertFromFinal = async (finalElement: any): Promise<PipelineElement[]> => {
    await ensureTasksLoaded();
    return deduplicateIds(_convertFromFinal(finalElement));
}

export const convertFromFinalList = (finalElement: any[]): PipelineElement[] => {
    return finalElement.map((element) => _convertFromFinal(element)).flat();
}


const addChildren = (type: string, element: PipelineElement) => {
    let definition = piplineConfig.find(config => config.type === type);
    if (definition?.generateEmptyElement)
        piplineConfig.find(config => config.type === element.type)!.addChildren2!(element, definition.generateEmptyElement());
};
const addChildren2 = (element: PipelineElement, target: PipelineElement) => {
    if (element.type === 'step') {
        (element as StepElement).tasks.push(target);
    } else
        (element as SkeletonElement).children.push(target);
};

export const piplineConfig: PipelineDefinition[] = [
    {
        type: 'skeleton',
        possibleNext: ['worker', 'variable', 'loadvars'],
        generateEmptyElement: (): SkeletonElement => {
            return {
                type: 'skeleton',
                id: crypto.randomUUID(),
                children: [],
            };
        },
        addChildren: addChildren,
        addChildren2: addChildren2,
        getChildren: (element: PipelineElement) => {
            return (element as SkeletonElement).children;
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            const elem = element as SkeletonElement;
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
            };
            if (config.getChildren) {
                finalElement.children = config.getChildren(element).map(value => convertFinal(value));
            }
            return finalElement;
        },
        parseElement: (element: any): SkeletonElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                children: (element.children || []).map((child: any) => _convertFromFinal(child)).flat(),
            }];
        },
        preview: (element: PipelineElement): string => {
            return `Skeleton`;
        },
        moveChildren: (element: PipelineElement, fromIndex: number, toIndex: number) => {
            const elem = element as SkeletonElement;
            if (fromIndex < 0 || fromIndex >= elem.children.length || toIndex < 0 || toIndex >= elem.children.length) {
                throw new Error('Invalid index for moving children');
            }
            const [movedChild] = elem.children.splice(fromIndex, 1);
            elem.children.splice(toIndex, 0, movedChild);
        },
        removeChildren: (element: PipelineElement, child: PipelineElement) => {
            const elem = element as SkeletonElement;
            const index = elem.children.indexOf(child);
            if (index !== -1) {
                elem.children.splice(index, 1);
            } else {
                throw new Error('Child not found in the element');
            }
        }
    },
    {
        type: 'variable',
        fields: {
            variables: {
                type: 'variable',
                label: 'Variables',
                placeholder: 'Set variable',
            }
        },
        generateEmptyElement: (): VariableElement => {
            return {
                type: 'variable',
                id: crypto.randomUUID(),
                variables: [],
            };
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            let variables: any = {};
            const elem = element as VariableElement;
            for (const vari of elem.variables) {
                variables[vari.key] = vari.value;
            }
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
                variables: variables,
            };
            if (config.getChildren) {
                finalElement.children = config.getChildren(element).map(value => convertFinal(value));
            }
            return finalElement;
        },
        parseElement: (element: any): VariableElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                variables: Object.entries(element.variables || {}).map(([key, value]) => ({
                    key: key as string,
                    value: value as string,
                    id: crypto.randomUUID(),
                })),
            }];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as VariableElement;
            return `Variables: ${fixedStringLength(elem.variables.map(v => `${v.key}=${v.value}`).join(', ') || 'None')}`;
        }
    },
    {
        type: 'conditionalvariable',
        fields: {
            branches: {
                type: 'branches',
                label: 'Conditions',
            }
        },
        generateEmptyElement: (): ConditionalVariableElement => {
            return {
                type: 'conditionalvariable',
                id: crypto.randomUUID(),
                branches: [],
                elseVariables: [],
            };
        },
        getFinalElement(element: PipelineElement): any {
            const elem = element as ConditionalVariableElement;
            return {
                type: elem.type,
                id: elem.id,
                branches: elem.branches.map(branch => {
                    let variables: any = {};
                    for (const vari of branch.variables) {
                        if (vari.key) variables[vari.key] = vari.value;
                    }
                    return {
                        condition: branch.condition.logic ?? {},
                        variables: variables,
                    };
                }),
                else: elem.elseVariables.reduce((acc: any, vari) => {
                    if (vari.key) acc[vari.key] = vari.value;
                    return acc;
                }, {}),
            };
        },
        parseElement: (element: any): ConditionalVariableElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                branches: (element.branches || []).map((branch: any) => ({
                    id: crypto.randomUUID(),
                    condition: {logic: branch.condition && Object.keys(branch.condition).length > 0 ? branch.condition : undefined},
                    variables: Object.entries(branch.variables || {}).map(([key, value]) => ({
                        key: key as string,
                        value: value as string,
                        id: crypto.randomUUID(),
                    })),
                })),
                elseVariables: Object.entries(element.else || {}).map(([key, value]) => ({
                    key: key as string,
                    value: value as string,
                    id: crypto.randomUUID(),
                })),
            }];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as ConditionalVariableElement;
            return `If/Else: ${elem.branches.length} condition(s)${elem.elseVariables.length > 0 ? ' + else' : ''}`;
        }
    },
    {
        type: 'loadvars',
        fields: {
            file: {
                type: 'string',
                label: 'File',
                placeholder: 'Enter path to the variables file',
            }
        },
        generateEmptyElement: (): LoadVarsElement => {
            return {
                type: 'loadvars',
                id: crypto.randomUUID(),
                file: '',
            };
        },
        getFinalElement(element: PipelineElement): any {
            const elem = element as LoadVarsElement;
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
                file: elem.file,
            };
            return finalElement;
        },
        parseElement: (element: any): LoadVarsElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                file: element.file || '',
            }];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as LoadVarsElement;
            return `Load Vars: ${fixedStringLength(elem.file || 'No File')}`;
        }
    },
    {
        type: 'worker',
        fields: {
            name: {
                type: 'string',
                label: 'Worker Name',
                placeholder: 'Enter worker name'
            },
            image: {
                type: 'dropdown',
                label: 'Worker Image',
                placeholder: 'Select worker image',
                options: ['none', 'build-container', 'build-container-java', 'build-container-node', 'build-container-cpp']
            },
            depends: {
                type: 'list',
                label: 'Depends on',
                placeholder: 'Select dependency',
            },
            options: {
                type: 'multiselect',
                label: 'Options',
                placeholder: 'Select worker options',
                options: ['volume'],
            },
            trigger: {
                type: 'trigger',
                label: 'Trigger',
                placeholder: 'Select trigger',
            }
        },
        possibleNext: ['worker', 'cmd', 'task', 'step', 'deployment', 'checkout', 'variable', 'loadvars', 'upload', 's3upload', 'asset', 'decryptfile', 'retrieve', 'createfile', 'putvarfile', 'addtestresults', 'grapsecrets', 'release', 'conditionalvariable'],
        generateEmptyElement: (): WorkerElement => {
            return {
                type: 'worker',
                id: crypto.randomUUID(),
                name: '',
                children: [],
                depends: [],
                options: [],
                trigger: {},
            };
        },
        addChildren: addChildren,
        addChildren2: addChildren2,
        getChildren: (element: PipelineElement) => {
            return (element as WorkerElement).children;
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            const elem = element as WorkerElement;
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
                name: elem.name,
            };
            if (config.getChildren) {
                finalElement.children = config.getChildren(element).map(value => convertFinal(value));
            }
            if (elem.image) {
                finalElement.image = elem.image;
            }
            if (elem.depends && elem.depends.length > 0) {
                finalElement.depends = elem.depends.map(dep => (dep.value));
            }
            if (elem.options && elem.options.length > 0) {
                finalElement.options = elem.options;
            }
            if (elem.trigger && Object.keys(elem.trigger).length > 0) {
                finalElement.trigger = elem.trigger as TriggerJson;
            }
            return finalElement;
        },
        parseElement: (element: any): WorkerElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                name: element.name,
                children: (element.children || []).map((child: any) => _convertFromFinal(child)).flat(),
                depends: element.depends ? element.depends.map((dep: string) => ({
                    value: dep,
                    id: crypto.randomUUID()
                })) : [],
                options: element.options || [],
                trigger: element.trigger || {},
                image: element.image,
            }];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as WorkerElement;
            return `Worker: ${elem.name || 'Unnamed'}`;
        },
        moveChildren: (element: PipelineElement, fromIndex: number, toIndex: number) => {
            const elem = element as SkeletonElement;
            if (fromIndex < 0 || fromIndex >= elem.children.length || toIndex < 0 || toIndex >= elem.children.length) {
                throw new Error('Invalid index for moving children');
            }
            const [movedChild] = elem.children.splice(fromIndex, 1);
            elem.children.splice(toIndex, 0, movedChild);
        },
        removeChildren: (element: PipelineElement, child: PipelineElement) => {
            const elem = element as SkeletonElement;
            const index = elem.children.indexOf(child);
            if (index !== -1) {
                elem.children.splice(index, 1);
            } else {
                throw new Error('Child not found in the element');
            }
        }
    },
    {
        type: 'step',
        fields: {
            name: {
                type: 'string',
                label: 'Step Name',
                placeholder: 'Enter step name'
            },
            locks: {
                type: 'list',
                label: 'Locks',
                placeholder: 'Add lock name',
            },
            trigger: {
                type: 'trigger',
                label: 'Trigger',
                placeholder: 'Select trigger',
            }
        },
        possibleNext: ['worker', 'cmd', 'task', 'step', 'deployment', 'checkout', 'variable', 'loadvars', 'upload', 's3upload', 'asset', 'decryptfile', 'retrieve', 'createfile', 'putvarfile', 'addtestresults', 'grapsecrets', 'release', 'conditionalvariable'],
        generateEmptyElement: (): StepElement => {
            return {
                type: 'step',
                id: crypto.randomUUID(),
                trigger: {},
                name: '',
                tasks: [],
                locks: [],
            };
        },

        addChildren: addChildren,
        addChildren2: addChildren2,
        getChildren: (element: PipelineElement) => {
            return (element as StepElement).tasks;
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            const elem = element as StepElement;
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
                name: elem.name,
            };
            if (config.getChildren) {
                finalElement.tasks = config.getChildren(element).map(value => convertFinal(value));
            }
            if (elem.trigger && Object.keys(elem.trigger).length > 0) {
                finalElement.trigger = elem.trigger as TriggerJson;
            }
            if (elem.locks && elem.locks.length > 0) {
                finalElement.locks = elem.locks.map(lock => lock.value);
            }
            return finalElement;
        },
        parseElement: (element: any): StepElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                name: element.name,
                tasks: (element.tasks || []).map((child: any) => _convertFromFinal(child)).flat(),
                trigger: element.trigger || {},
                locks: (element.locks || []).map((lock: string) => ({
                    value: lock,
                    id: crypto.randomUUID(),
                })),
            }];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as WorkerElement;
            return `Step: ${elem.name || 'Unnamed'}`;
        },
        moveChildren: (element: PipelineElement, fromIndex: number, toIndex: number) => {
            const elem = element as StepElement;
            if (fromIndex < 0 || fromIndex >= elem.tasks.length || toIndex < 0 || toIndex >= elem.tasks.length) {
                throw new Error('Invalid index for moving children');
            }
            const [movedChild] = elem.tasks.splice(fromIndex, 1);
            elem.tasks.splice(toIndex, 0, movedChild);
        },
        removeChildren: (element: PipelineElement, child: PipelineElement) => {
            const elem = element as StepElement;
            const index = elem.tasks.indexOf(child);
            if (index !== -1) {
                elem.tasks.splice(index, 1);
            } else {
                throw new Error('Child not found in the element');
            }
        }
    },
    {
        type: 'cmd',
        fields: {
            cmd: {
                type: 'cmd',
                label: 'Command',
                placeholder: 'Enter command to execute'
            },
            flags: {
                type: 'multiselect',
                label: 'Flags',
                placeholder: 'Select flags',
                options: ['ignore-errors', 'warn-on-failure'],
            },
            exitCodeVar: {
                type: 'string',
                label: 'Write exit code to var',
                placeholder: 'Variable name (optional)',
            },
            logVar: {
                type: 'string',
                label: 'Write log to var',
                placeholder: 'Variable name (optional)',
            }
        },
        generateEmptyElement: (): CmdElement => {
            return {
                type: 'cmd',
                id: crypto.randomUUID(),
                cmd: '',
                flags: [],
                exitCodeVar: '',
                logVar: '',
            };
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            const elem = element as CmdElement;
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
                cmd: [elem.cmd]
            };
            if (elem.flags && elem.flags.length > 0) {
                finalElement.flags = elem.flags;
            }
            if (elem.exitCodeVar) {
                finalElement.exitCodeVar = elem.exitCodeVar;
            }
            if (elem.logVar) {
                finalElement.logVar = elem.logVar;
            }
            return finalElement;
        },
        parseElement: (element: any): CmdElement[] => {
            return element.cmd.map((c: string, index: number) => ({
                type: element.type,
                id: element.id && index <= 0 ? element.id : crypto.randomUUID(),
                cmd: c,
                flags: element.flags ? element.flags : [],
                exitCodeVar: element.exitCodeVar || '',
                logVar: element.logVar || '',
            })) as CmdElement[];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as CmdElement;
            return `Cmd: ${fixedStringLength(elem.cmd || 'Empty Command')}`;
        },
    },
    {
        type: 'grapsecrets',
        fields: {
            files: {
                type: 'list',
                label: 'Files',
                placeholder: 'Add files to download (supports wildcards, e.g. /folder/*), leave empty for all',
            }
        },
        generateEmptyElement: (): GrabSecretsElement => {
            return {
                type: 'grapsecrets',
                id: crypto.randomUUID(),
                files: [],
            };
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            const elem = element as GrabSecretsElement;
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
            };
            if (elem.files && elem.files.length > 0) {
                finalElement.files = elem.files.map(value => value.value);
            }
            return finalElement;
        },
        parseElement: (element: any): GrabSecretsElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                files: (element.files || []).map((value: string) => ({
                    value: value,
                    id: crypto.randomUUID(),
                })),
            }] as GrabSecretsElement[];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as GrabSecretsElement;
            if (elem.files && elem.files.length > 0) {
                return `Grap Secrets: ${fixedStringLength(elem.files.map(value => value.value).join(', '))}`;
            }
            return `Grap Secrets: All`;
        },
    },
    {
        type: 'task',
        fields: {
            tasks: {
                type: 'dropdown',
                label: 'Task',
                placeholder: 'Enter task name',
                options: ['none'],
            },
        },
        generateEmptyElement: (): TaskElement => {
            return {
                type: 'task',
                id: crypto.randomUUID(),
                tasks: '',
                children: [],
            };
        },
        getChildren(element: PipelineElement) {
            const task = element as TaskElement;
            return task.children || [];
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            const elem = element as TaskElement;
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
                tasks: [elem.tasks]
            };
            return finalElement;
        },
        parseElement: (element: any): TaskElement[] => {
            return element.tasks.map((c: string, index: number) => {
                const ogTask = tasks?.find(t => t.name.toLowerCase() === c?.toLowerCase());
                if (!ogTask) {
                    return {
                        type: element.type,
                        id: element.id && index <= 0 ? element.id : crypto.randomUUID(),
                        tasks: c,
                        children: [],
                    };
                }
                let converted = convertFromFinalList(JSON.parse(ogTask.ci));
                for (const child of converted) {
                    addPrefix(element.id, child);
                }
                return {
                    type: element.type,
                    id: element.id && index <= 0 ? element.id : crypto.randomUUID(),
                    tasks: c,
                    children: converted,
                };
            }) as TaskElement[];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as TaskElement;
            return `Task: ${elem.tasks || 'No Task Defined'}`;
        },

    },
    {
        type: 'deployment',
        fields: {
            name: {
                type: 'string',
                label: 'Deployment Name',
                placeholder: 'Enter deployment name',
            },
            token: {
                type: 'string',
                label: 'Token',
                placeholder: 'Enter deployment token',
            },
            depends: {
                type: 'list',
                label: 'Depends on',
                placeholder: 'Select dependency',
            }
        },
        generateEmptyElement: (): DeploymentElement => {
            return {
                type: 'deployment',
                id: crypto.randomUUID(),
                name: '',
                depends: [],
                token: '',
                down: {type: 'deployment-down', id: crypto.randomUUID(), children: []},
                update: {type: 'deployment-update', id: crypto.randomUUID(), children: []},
                up: {type: 'deployment-up', id: crypto.randomUUID(), children: []},
                status: {type: 'deployment-status', id: crypto.randomUUID(), children: []},
            };
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            const elem = element as DeploymentElement;
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
                name: elem.name,
                token: elem.token,
            };
            if (elem.depends && elem.depends.length > 0) {
                finalElement.depends = elem.depends.map(dep => (dep.value));
            }
            const getChildren = (element: DeploymentPipelineElement) => element.children;
            finalElement.down = getChildren(elem.down).map(value => convertFinal(value));
            finalElement.up = getChildren(elem.up).map(value => convertFinal(value));
            finalElement.update = getChildren(elem.update).map(value => convertFinal(value));
            finalElement.status = getChildren(elem.status).map(value => convertFinal(value));

            return finalElement;
        },
        getChildren: (element: PipelineElement) => {
            return [
                (element as DeploymentElement).down,
                (element as DeploymentElement).up,
                (element as DeploymentElement).update,
                (element as DeploymentElement).status
            ];
        },
        parseElement: (element: any): DeploymentElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                name: element.name,
                token: element.token,
                depends: element.depends ? element.depends.map((dep: string) => ({
                    value: dep,
                    id: crypto.randomUUID()
                })) : [],
                down: {
                    type: 'deployment-down',
                    id: crypto.randomUUID(),
                    children: (element.down || []).map((child: any) => _convertFromFinal(child)).flat(),
                },
                up: {
                    type: 'deployment-up',
                    id: crypto.randomUUID(),
                    children: (element.up || []).map((child: any) => _convertFromFinal(child)).flat(),
                },
                update: {
                    type: 'deployment-update',
                    id: crypto.randomUUID(),
                    children: (element.update || []).map((child: any) => _convertFromFinal(child)).flat(),
                },
                status: {
                    type: 'deployment-status',
                    id: crypto.randomUUID(),
                    children: (element.status || []).map((child: any) => _convertFromFinal(child)).flat(),
                },
            }];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as DeploymentElement;
            return `Deployment: ${elem.name}`;
        },
    },
    {
        type: 'checkout',
        fields: {
            repo: {
                type: 'repo',
                label: 'Repository',
                placeholder: 'Enter repository name'
            },
            dir: {
                type: 'string',
                label: 'Directory',
                placeholder: 'Enter directory path'
            },
            files: {
                type: 'list',
                label: 'Files',
                placeholder: 'Repository-relative file or directory; leave empty for all'
            }
        },
        generateEmptyElement: (): CheckoutElement => {
            return {
                type: 'checkout',
                id: crypto.randomUUID(),
                repo: '',
                dir: '',
                files: [],
            };
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            let elem = element as CheckoutElement;
            let finalElement: any = {
                type: element.type,
                id: element.id,
            };
            if (elem.repo) {
                finalElement.repo = elem.repo;
            }
            if (elem.dir) {
                finalElement.dir = elem.dir;
            }
            if (elem.files && elem.files.length > 0) {
                finalElement.files = elem.files.map(file => file.value);
            }

            return finalElement;
        },
        parseElement: (element: any): CheckoutElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                repo: element.repo || '',
                dir: element.dir || '',
                files: (element.files || []).map((file: string) => ({
                    value: file,
                    id: crypto.randomUUID(),
                })),
            }];
        },
        preview: (element: PipelineElement): string => {
            return `Checkout`;
        }
    },
    {
        type: 'release',
        fields: {
            repo: {
                type: 'repo',
                label: 'Repository',
                placeholder: 'Enter repository name'
            },
            tag: {
                type: 'string',
                label: 'Tag',
                placeholder: 'e.g. v${version} or ${commitIdShort}'
            },
            title: {
                type: 'string',
                label: 'Title',
                placeholder: 'Enter release title'
            },
            content: {
                type: 'cmd',
                label: 'Content',
                placeholder: 'Enter release notes'
            },
            files: {
                type: 'list',
                label: 'Files',
                placeholder: 'Add files from the asset folder',
            },
            draft: {
                type: 'boolean',
                label: 'Draft',
            },
            prerelease: {
                type: 'boolean',
                label: 'Prerelease',
            }
        },
        generateEmptyElement: (): ReleaseElement => {
            return {
                type: 'release',
                id: crypto.randomUUID(),
                repo: '',
                tag: '',
                title: '',
                content: '',
                files: [],
                draft: false,
                prerelease: false,
            };
        },
        getFinalElement(element: PipelineElement): any {
            const elem = element as ReleaseElement;
            const finalElement: any = {
                type: element.type,
                id: element.id,
                tag: elem.tag || '',
                title: elem.title || '',
                content: elem.content || '',
                files: (elem.files || []).map(value => value.value),
            };
            if (elem.repo) {
                finalElement.repo = elem.repo;
            }
            if (elem.draft) {
                finalElement.draft = true;
            }
            if (elem.prerelease) {
                finalElement.prerelease = true;
            }

            return finalElement;
        },
        parseElement: (element: any): ReleaseElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                repo: element.repo || '',
                tag: element.tag || '',
                title: element.title || '',
                content: element.content || '',
                files: (element.files || []).map((value: string) => ({
                    value: value,
                    id: crypto.randomUUID(),
                })),
                draft: element.draft || false,
                prerelease: element.prerelease || false,
            }];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as ReleaseElement;
            return `Release: ${fixedStringLength(elem.tag || 'No Tag')}`;
        }
    },

    {
        type: 'deployment-down',
        generateEmptyElement: (): DeploymentDownElement => {
            return {
                type: 'deployment-down',
                id: crypto.randomUUID(),
                children: [],
            };
        },
        getChildren: (element: PipelineElement) => {
            return (element as DeploymentDownElement).children;
        },
        preview: (element: PipelineElement): string => {
            return `- Down:`;
        },
        getFinalElement: () => {
            throw new Error('DeploymentDownElement does not support final conversion');
        },
        parseElement: (element: any): DeploymentDownElement[] => {
            throw new Error('DeploymentDownElement does not support final conversion');
        },
        canBeRemoved: (elmement: PipelineElement): boolean => {
            return false;
        },
        possibleNext: ['worker', 'cmd', 'task', 'step', 'deployment', 'checkout', 'variable', 'loadvars', 'upload', 's3upload', 'asset', 'decryptfile', 'retrieve', 'createfile', 'putvarfile', 'addtestresults', 'grapsecrets', 'release', 'conditionalvariable'],
        addChildren: addChildren,
        addChildren2: addChildren2,
        moveChildren: (element: PipelineElement, fromIndex: number, toIndex: number) => {
            const elem = element as SkeletonElement;
            if (fromIndex < 0 || fromIndex >= elem.children.length || toIndex < 0 || toIndex >= elem.children.length) {
                throw new Error('Invalid index for moving children');
            }
            const [movedChild] = elem.children.splice(fromIndex, 1);
            elem.children.splice(toIndex, 0, movedChild);
        },
        removeChildren: (element: PipelineElement, child: PipelineElement) => {
            const elem = element as SkeletonElement;
            const index = elem.children.indexOf(child);
            if (index !== -1) {
                elem.children.splice(index, 1);
            } else {
                throw new Error('Child not found in the element');
            }
        }
    },

    {
        type: 'deployment-up',
        generateEmptyElement: (): DeploymentUpElement => {
            return {
                type: 'deployment-up',
                id: crypto.randomUUID(),
                children: [],
            };
        },
        getChildren: (element: PipelineElement) => {
            return (element as DeploymentUpElement).children;
        },
        preview: (element: PipelineElement): string => {
            return `- Up:`;
        },
        getFinalElement: () => {
            throw new Error('DeploymentUpElement does not support final conversion');
        },
        parseElement: (element: any): DeploymentUpElement[] => {
            throw new Error('DeploymentUpElement does not support final conversion');
        },
        canBeRemoved: (elmement: PipelineElement): boolean => {
            return false;
        },
        possibleNext: ['worker', 'cmd', 'task', 'step', 'deployment', 'checkout', 'variable', 'loadvars', 'upload', 's3upload', 'asset', 'decryptfile', 'retrieve', 'createfile', 'putvarfile', 'addtestresults', 'grapsecrets', 'release', 'conditionalvariable'],
        addChildren: addChildren,
        addChildren2: addChildren2,
        moveChildren: (element: PipelineElement, fromIndex: number, toIndex: number) => {
            const elem = element as SkeletonElement;
            if (fromIndex < 0 || fromIndex >= elem.children.length || toIndex < 0 || toIndex >= elem.children.length) {
                throw new Error('Invalid index for moving children');
            }
            const [movedChild] = elem.children.splice(fromIndex, 1);
            elem.children.splice(toIndex, 0, movedChild);
        },
        removeChildren: (element: PipelineElement, child: PipelineElement) => {
            const elem = element as SkeletonElement;
            const index = elem.children.indexOf(child);
            if (index !== -1) {
                elem.children.splice(index, 1);
            } else {
                throw new Error('Child not found in the element');
            }
        }
    },

    {
        type: 'deployment-status',
        generateEmptyElement: (): DeploymentStatusElement => {
            return {
                type: 'deployment-status',
                id: crypto.randomUUID(),
                children: [],
            };
        },
        getChildren: (element: PipelineElement) => {
            return (element as DeploymentStatusElement).children;
        },
        preview: (element: PipelineElement): string => {
            return `- Status:`;
        },
        getFinalElement: () => {
            throw new Error('DeploymentStatusElement does not support final conversion');
        },
        parseElement: (element: any): DeploymentStatusElement[] => {
            throw new Error('DeploymentStatusElement does not support final conversion');
        },
        canBeRemoved: (elmement: PipelineElement): boolean => {
            return false;
        },
        possibleNext: ['worker', 'cmd', 'task', 'step', 'deployment', 'checkout', 'variable', 'loadvars', 'upload', 's3upload', 'asset', 'decryptfile', 'retrieve', 'createfile', 'putvarfile', 'addtestresults', 'grapsecrets', 'release', 'conditionalvariable'],
        addChildren: addChildren,
        addChildren2: addChildren2,
        moveChildren: (element: PipelineElement, fromIndex: number, toIndex: number) => {
            const elem = element as SkeletonElement;
            if (fromIndex < 0 || fromIndex >= elem.children.length || toIndex < 0 || toIndex >= elem.children.length) {
                throw new Error('Invalid index for moving children');
            }
            const [movedChild] = elem.children.splice(fromIndex, 1);
            elem.children.splice(toIndex, 0, movedChild);
        },
        removeChildren: (element: PipelineElement, child: PipelineElement) => {
            const elem = element as SkeletonElement;
            const index = elem.children.indexOf(child);
            if (index !== -1) {
                elem.children.splice(index, 1);
            } else {
                throw new Error('Child not found in the element');
            }
        }
    },

    {
        type: 'deployment-update',
        generateEmptyElement: (): DeploymentUpdateElement => {
            return {
                type: 'deployment-update',
                id: crypto.randomUUID(),
                children: [],
            };
        },
        getChildren: (element: PipelineElement) => {
            return (element as DeploymentUpdateElement).children;
        },
        preview: (element: PipelineElement): string => {
            return `- Update:`;
        },
        getFinalElement: () => {
            throw new Error('DeploymentUpdateElement does not support final conversion');
        },
        parseElement: (element: any): DeploymentUpdateElement[] => {
            throw new Error('DeploymentUpdateElement does not support final conversion');
        },
        canBeRemoved: (elmement: PipelineElement): boolean => {
            return false;
        },
        possibleNext: ['worker', 'cmd', 'task', 'step', 'deployment', 'checkout', 'variable', 'loadvars', 'upload', 's3upload', 'asset', 'decryptfile', 'retrieve', 'createfile', 'putvarfile', 'addtestresults', 'grapsecrets', 'release', 'conditionalvariable'],
        addChildren: addChildren,
        addChildren2: addChildren2,
        moveChildren: (element: PipelineElement, fromIndex: number, toIndex: number) => {
            const elem = element as SkeletonElement;
            if (fromIndex < 0 || fromIndex >= elem.children.length || toIndex < 0 || toIndex >= elem.children.length) {
                throw new Error('Invalid index for moving children');
            }
            const [movedChild] = elem.children.splice(fromIndex, 1);
            elem.children.splice(toIndex, 0, movedChild);
        },
        removeChildren: (element: PipelineElement, child: PipelineElement) => {
            const elem = element as SkeletonElement;
            const index = elem.children.indexOf(child);
            if (index !== -1) {
                elem.children.splice(index, 1);
            } else {
                throw new Error('Child not found in the element');
            }
        }
    },
    {
        type: 'asset',
        fields: {
            prefix: {
                type: 'string',
                label: 'Prefix',
                placeholder: 'Enter prefix for asset files',
            },
            files: {
                type: 'list',
                label: 'Files',
                placeholder: 'Add files to asset',
            },
            flags: {
                type: 'multiselect',
                label: 'Flags',
                placeholder: 'Select flags',
                options: ['ignore-errors', 'warn-on-failure'],
            }
        },
        generateEmptyElement: (): AssetElement => {
            return {
                type: 'asset',
                id: crypto.randomUUID(),
                files: [],
                prefix: '',
                flags: [],
            };
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            const elem = element as AssetElement;
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
                files: elem.files.map(value => value.value),
                prefix: elem.prefix,
            };
            if (elem.flags && elem.flags.length > 0) {
                finalElement.flags = elem.flags;
            }
            return finalElement;
        },
        parseElement: (element: any): AssetElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                files: element.files.map((value: string) => ({
                    value: value,
                    id: crypto.randomUUID(),
                })) || [],
                prefix: element.prefix || '',
                flags: element.flags ? element.flags : [],
            }];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as AssetElement;
            return `Asset: ${fixedStringLength(elem.files.map(value => value.value).join(', ') || 'No Files')}`;
        },
    },
    {
        type: 's3upload',
        fields: {
            from: {type: 'string', label: 'Source asset path', placeholder: 'assets/dist (collect files with an Asset block first)'},
            bucket: {type: 'string', label: 'S3 bucket'},
            region: {type: 'string', label: 'Region', placeholder: 'us-east-1'},
            keyPrefix: {type: 'string', label: 'Object key prefix', placeholder: 'releases/ (optional)'},
            endpoint: {type: 'string', label: 'S3-compatible endpoint', placeholder: 'Leave empty for AWS S3'},
            pathStyle: {type: 'boolean', label: 'Use path-style addressing'},
            accessKey: {type: 'secret', label: 'Access key'},
            secretKey: {type: 'secret', label: 'Secret key'},
        },
        generateEmptyElement: (): S3UploadElement => ({
            type: 's3upload', id: crypto.randomUUID(), from: '', bucket: '', region: 'us-east-1',
            keyPrefix: '', endpoint: '', pathStyle: false, accessKey: '', secretKey: '', deployment: true,
        }),
        getFinalElement: (element: PipelineElement) => {
            const e = element as S3UploadElement;
            return {type: e.type, id: e.id, from: e.from, bucket: e.bucket, region: e.region,
                keyPrefix: e.keyPrefix, endpoint: e.endpoint, pathStyle: e.pathStyle,
                accessKey: e.accessKey, secretKey: e.secretKey};
        },
        parseElement: (e: any): S3UploadElement[] => [{
            type: 's3upload', id: e.id || crypto.randomUUID(), from: e.from || '', bucket: e.bucket || '',
            region: e.region ?? 'us-east-1', keyPrefix: e.keyPrefix || '', endpoint: e.endpoint || '',
            pathStyle: !!e.pathStyle, accessKey: e.accessKey || '', secretKey: e.secretKey || '',
        }],
        preview: (element: PipelineElement) => `S3 upload: ${(element as S3UploadElement).from || 'No source'} → ${(element as S3UploadElement).bucket || 'No bucket'}`,
    },
    {
        type: 'upload',
        fields: {
            from: {
                type: 'string',
                label: 'From',
                placeholder: 'Enter source path',
            },
            to: {
                type: 'string',
                label: 'To',
                placeholder: 'Enter destination path',
            }
        },
        generateEmptyElement: (): UploadElement => {
            return {
                type: 'upload',
                id: crypto.randomUUID(),
                from: '',
                to: '',
            };
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            const elem = element as UploadElement;
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
                from: elem.from,
                to: elem.to,
            };
            return finalElement;
        },
        parseElement: (element: any): UploadElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                from: element.from ?? '',
                to: element.to ?? '',
            }];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as UploadElement;
            return `Upload: ${fixedStringLength(elem.from ? elem.from : 'No Source')} to ${fixedStringLength(elem.to ? elem.to : 'No Destination')}`;
        },
    },
    {
        type: 'createfile',
        fields: {
            file: {
                type: 'string',
                label: 'file',
                placeholder: 'Enter file path',
            },
            content: {
                type: 'cmd',
                label: 'Content',
                placeholder: 'Enter file content',
            }
        },
        generateEmptyElement: (): CreateFileElement => {
            return {
                type: 'createfile',
                id: crypto.randomUUID(),
                file: '',
                content: '',
            };
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            const elem = element as CreateFileElement;
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
                file: elem.file,
                content: elem.content,
            };
            return finalElement;
        },
        parseElement: (element: any): CreateFileElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                file: element.file ?? '',
                content: element.content ?? '',
            }];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as CreateFileElement;
            return `CreateFile: ${fixedStringLength(elem.file ? elem.file : 'No File')}`;
        },
    },
    {
        type: 'decryptfile',
        fields: {
            files: {
                type: 'list',
                label: 'Files',
                placeholder: 'Add files to asset',
            }
        },
        generateEmptyElement: (): DecryptFilesElement => {
            return {
                type: 'decryptfile',
                id: crypto.randomUUID(),
                files: [],
            };
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            const elem = element as DecryptFilesElement;
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
                files: elem.files.map(value => value.value),
            };
            return finalElement;
        },
        parseElement: (element: any): DecryptFilesElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                files: element.files.map((value: string) => ({
                    value: value,
                    id: crypto.randomUUID(),
                })) || [],
            }];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as DecryptFilesElement;
            return `Decrypt Files: ${fixedStringLength(elem.files.map(value => value.value).join(', ') || 'No Files')}`;
        },
    },
    {
        type: 'taskeditor',
        fields: {
            name: {
                type: 'string',
                label: 'Task Name',
                placeholder: 'Enter task name'
            }
        },
        possibleNext: ['worker', 'cmd', 'task', 'step', 'deployment', 'checkout', 'variable', 'loadvars', 'upload', 's3upload', 'asset', 'decryptfile', 'retrieve', 'createfile', 'putvarfile', 'grapsecrets', 'addtestresults', 'release', 'conditionalvariable'],
        generateEmptyElement: (): TaskEditorElement => {
            return {
                type: 'taskeditor',
                id: crypto.randomUUID(),
                name: '',
                children: [],
            };
        },
        addChildren: addChildren,
        addChildren2: addChildren2,
        getChildren: (element: PipelineElement) => {
            return (element as WorkerElement).children;
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            const elem = element as TaskEditorElement;
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
                name: elem.name,
            };
            if (config.getChildren) {
                finalElement.children = config.getChildren(element).map(value => convertFinal(value));
            }
            return finalElement;
        },
        parseElement: (element: any): TaskEditorElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                name: element.name,
                children: (element.children || []).map((child: any) => _convertFromFinal(child)).flat(),
            }];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as WorkerElement;
            return `Task: ${elem.name || 'Unnamed'}`;
        },
        moveChildren: (element: PipelineElement, fromIndex: number, toIndex: number) => {
            const elem = element as TaskEditorElement;
            if (fromIndex < 0 || fromIndex >= elem.children.length || toIndex < 0 || toIndex >= elem.children.length) {
                throw new Error('Invalid index for moving children');
            }
            const [movedChild] = elem.children.splice(fromIndex, 1);
            elem.children.splice(toIndex, 0, movedChild);
        },
        removeChildren: (element: PipelineElement, child: PipelineElement) => {
            const elem = element as TaskEditorElement;
            const index = elem.children.indexOf(child);
            if (index !== -1) {
                elem.children.splice(index, 1);
            } else {
                throw new Error('Child not found in the element');
            }
        }
    },


    {
        type: 'retrieve',
        fields: {
            fromRepo: {
                type: 'repo',
                label: 'From Repository',
                placeholder: 'Enter repository name'
            },
            file: {
                type: 'string',
                label: 'File',
                placeholder: 'Enter file path'
            },
            target: {
                type: 'string',
                label: 'Target',
                placeholder: 'Enter target path'
            }
        },
        generateEmptyElement: (): RetrieveElement => {
            return {
                type: 'retrieve',
                id: crypto.randomUUID(),
                fromRepo: '',
                file: '',
                target: '',
            };
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            const elem = element as RetrieveElement;
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
                fromRepo: elem.fromRepo,
                file: elem.file,
                target: elem.target,
            };
            return finalElement;
        },
        parseElement: (element: any): RetrieveElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                fromRepo: element.fromRepo || '',
                file: element.file || '',
                target: element.target || '',
            }];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as RetrieveElement;
            return `Retrieve: ${fixedStringLength((elem.file ? elem.file : 'No Source') + " to " + (elem.target ? elem.target : 'No Destination'))}`;
        },
    },
    {
        type: 'putvarfile',
        fields: {
            files: {
                type: 'list',
                label: 'Files',
                placeholder: 'Add files to asset',
            }
        },
        generateEmptyElement: (): PutVarFileElement => {
            return {
                type: 'putvarfile',
                id: crypto.randomUUID(),
                files: [],
            };
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            const elem = element as PutVarFileElement;
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
                files: elem.files.map(value => value.value),
            };
            return finalElement;
        },
        parseElement: (element: any): PutVarFileElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                files: element.files.map((value: string) => ({
                    value: value,
                    id: crypto.randomUUID(),
                })) || [],
            }];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as PutVarFileElement;
            return `Put Var File: ${fixedStringLength(elem.files.map(value => value.value).join(', ') || 'No Files')}`;
        },
    },
    {
        type: 'addtestresults',
        fields: {
            file: {
                type: 'string',
                label: 'File',
                placeholder: 'Enter path to the test results HTML file',
            }
        },
        generateEmptyElement: (): AddTestResultsElement => {
            return {
                type: 'addtestresults',
                id: crypto.randomUUID(),
                file: '',
            };
        },
        getFinalElement(element: PipelineElement): any {
            const config = piplineConfig.find(config => config.type === element.type);
            if (!config) {
                throw new Error(`No configuration found for type: ${element.type}`);
            }
            const elem = element as AddTestResultsElement;
            const finalElement: any = {
                type: elem.type,
                id: elem.id,
                file: elem.file,
            };
            return finalElement;
        },
        parseElement: (element: any): AddTestResultsElement[] => {
            return [{
                type: element.type,
                id: element.id ? element.id : crypto.randomUUID(),
                file: element.file || '',
            }];
        },
        preview: (element: PipelineElement): string => {
            const elem = element as AddTestResultsElement;
            return `Add Test Results: ${fixedStringLength(elem.file || 'No File')}`;
        },
    },
];

const addPrefix = (prefix: string, element: PipelineElement) => {
    element.id = prefix+'/'+element.id;
    const config = piplineConfig.find(config => config.type === element.type);
    if (!config) {
        throw new Error(`No configuration found for type: ${element.type}`);
    }
    if (config.getChildren) {
        const children = config.getChildren(element);
        for (const child of children) {
            addPrefix(prefix, child);
        }
    }
}

const fixedStringLength = (value: string): string => {
    const maxLength = 50; // Adjust as needed
    if (value.length > maxLength) {
        return value.substring(0, maxLength) + '...';
    }
    return value;
}

export interface PipelineElement {
    type: string;
    name?: string;
    id: string;

    // Marks this block (and everything nested inside it) as a real deployment action. Test
    // runs driven by the MCP server always skip deployment-marked blocks instead of executing
    // them, so an AI testing a pipeline can never accidentally trigger a real deployment. Real
    // (non-test) runs are unaffected. Generic to every block type - see convertFinal/
    // _convertFromFinal below, which carry it through independently of each type's own
    // getFinalElement/parseElement.
    deployment?: boolean;

    lastStatus?: 'finished' | 'running' | 'error' | 'warning' | 'waiting' | 'canceled';
    logs?: { level: string, message: string }[];
}

export interface VariableField {
    key: string;
    value: string;
    id: string;
}

export interface ListField {
    value: string;
    id: string;
}

export interface SkeletonElement extends PipelineElement {
    children: PipelineElement[];
}

export interface WorkerElement extends PipelineElement {
    children: PipelineElement[];
    depends: ListField[];
    options: string[];
    image?: string;
    trigger: TriggerJson;
}

export interface StepElement extends PipelineElement {
    tasks: PipelineElement[];
    trigger: TriggerJson;
    name: string;
    locks?: ListField[];
}

export interface VariableElement extends PipelineElement {
    variables: VariableField[];
}

export interface LoadVarsElement extends PipelineElement {
    file: string;
}

export interface CheckoutElement extends PipelineElement {
    repo: string;
    dir: string;
    files: ListField[];
}

export interface ConditionalVariableBranch {
    id: string;
    condition: TriggerJson;
    variables: VariableField[];
}

export interface ConditionalVariableElement extends PipelineElement {
    branches: ConditionalVariableBranch[];
    elseVariables: VariableField[];
}

export interface ReleaseElement extends PipelineElement {
    repo: string;
    tag: string;
    title: string;
    content: string;
    files: ListField[];
    draft: boolean;
    prerelease: boolean;
}

export interface RetrieveElement extends PipelineElement {
    fromRepo: string;
    file: string;
    target: string;
}

export interface CmdElement extends PipelineElement {
    cmd: string;
    flags: string[];
    exitCodeVar?: string;
    logVar?: string;
}

export interface TaskElement extends PipelineElement {
    tasks: string;
    children: PipelineElement[];
}

export interface DeploymentElement extends PipelineElement {
    name: string;
    down: DeploymentDownElement;
    up: DeploymentUpElement;
    update: DeploymentUpdateElement;
    status: DeploymentStatusElement;
    depends: ListField[];
    token: string;
}

export interface DeploymentPipelineElement extends PipelineElement {
    children: PipelineElement[];
}

export interface DeploymentDownElement extends DeploymentPipelineElement {
}

export interface DeploymentUpElement extends DeploymentPipelineElement {
}

export interface DeploymentUpdateElement extends DeploymentPipelineElement {
}

export interface DeploymentStatusElement extends DeploymentPipelineElement {
}

export interface GrabSecretsElement extends PipelineElement {
    files?: ListField[];
}

export interface AssetElement extends PipelineElement {
    files: ListField[];
    prefix: string;
    flags: string[];
}

export interface UploadElement extends PipelineElement {
    from: string;
    to: string;
}

export interface CreateFileElement extends PipelineElement {
    file: string;
    content: string;
}

export interface DecryptFilesElement extends PipelineElement {
    files: ListField[];
}

export interface PutVarFileElement extends PipelineElement {
    files: ListField[];
}

export interface AddTestResultsElement extends PipelineElement {
    file: string;
}

export interface TaskEditorElement extends PipelineElement {
    children: PipelineElement[];
}

export interface S3UploadElement extends PipelineElement {
    from: string;
    bucket: string;
    region: string;
    keyPrefix: string;
    endpoint: string;
    pathStyle: boolean;
    accessKey: string;
    secretKey: string;
}
